<script>
  import { getApiBase } from './apiBase.js';

  export let teacherId;
  export let teacherName;
  export let month;
  export let bonusEligible;
  export let bonusAmount;

  let container;
  let open = false;
  let loading = false;
  let error = '';
  let coverage = null;
  let openedWithKeyboard = false;
  let openAbove = false;
  let requestVersion = 0;
  let previousMonth = month;

  $: if (month !== previousMonth) {
    previousMonth = month;
    close();
  }

  function close() {
    open = false;
    openedWithKeyboard = false;
    loading = false;
    coverage = null;
    error = '';
    requestVersion += 1;
  }

  function show(event) {
    if (event?.type === 'focus') openedWithKeyboard = true;
    if (!open) loadCoverage();
  }

  async function loadCoverage() {
    const bounds = container?.getBoundingClientRect();
    openAbove = bounds && window.innerHeight - bounds.bottom < 300 && bounds.top > 300;
    open = true;
    loading = true;
    error = '';
    coverage = null;
    const version = ++requestVersion;
    const requestedMonth = month;
    const params = new URLSearchParams({ teacher_id: String(teacherId), month: requestedMonth });
    try {
      const response = await fetch(`${getApiBase()}/studentplayground/api/teacher_comment_coverage/?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken') || ''}` },
        cache: 'no-store',
      });
      if (!response.ok) throw new Error('Could not load class feedback.');
      const data = await response.json();
      if (version === requestVersion && requestedMonth === month && open) coverage = data;
    } catch (cause) {
      if (version === requestVersion && requestedMonth === month && open) error = cause.message;
    } finally {
      if (version === requestVersion && requestedMonth === month && open) loading = false;
    }
  }

  function handleMouseLeave() {
    if (!loading && !openedWithKeyboard && !container?.contains(document.activeElement)) close();
  }

  function handleOutsideClick(event) {
    if (open && !event.composedPath().includes(container)) close();
  }

  function handleKeydown(event) {
    if (open && event.key === 'Escape') close();
  }

  function formatClassTime(value) {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Vilnius', dateStyle: 'medium', timeStyle: 'short',
    }).format(new Date(value));
  }
</script>

<svelte:window on:click={handleOutsideClick} on:keydown={handleKeydown} />

<div class="bonus-detail" bind:this={container} on:mouseleave={handleMouseLeave}>
  <button type="button" class="bonus-trigger" aria-label={`Class feedback for ${teacherName}`} aria-expanded={open} on:mouseenter={show} on:focus={show} on:click={show}>
    {#if bonusEligible}
      <span class="bonus-yes">✅ €{bonusAmount}</span>
    {:else}
      <span class="bonus-no">—</span>
    {/if}
    <span class="detail-indicator" aria-hidden="true">ⓘ</span>
  </button>
  {#if open}
    <div class="coverage-popover" class:above={openAbove} role="dialog" aria-label={`Class feedback for ${teacherName}`}>
      <div class="popover-header">
        <strong>Class feedback · {teacherName}</strong>
        <button type="button" class="close-button" aria-label="Close class feedback details" on:click={close}>×</button>
      </div>
      {#if loading}
        <p role="status">Loading class feedback…</p>
      {:else if error}
        <p role="alert">{error}</p>
        <button type="button" on:click={loadCoverage}>Try again</button>
      {:else if coverage}
        <p class="coverage-summary"><strong>{coverage.submitted_count} submitted</strong> · <strong>{coverage.missing_count} missing</strong> of {coverage.total_classes} classes</p>
        <p class="snapshot-note">Feedback status is live. Bonus eligibility and amount use the saved payment snapshot.</p>
        {#if coverage.total_classes === 0}
          <p>No classes in this month.</p>
        {:else if coverage.missing_count === 0}
          <p>All class feedback submitted.</p>
        {:else}
          <p class="list-label">Classes missing feedback</p>
          <ul class="missing-list">
            {#each coverage.missing_classes as cls (cls.class_id)}
              <li>
                <span class="class-name">{cls.class_name}</span>
                <span class="class-meta">{formatClassTime(cls.class_time)}{#if cls.upcoming} · Upcoming{/if}</span>
              </li>
            {/each}
          </ul>
        {/if}
      {/if}
    </div>
  {/if}
</div>

<style>
  .bonus-detail { position: relative; display: inline-block; }
  .bonus-trigger { display: inline-flex; align-items: center; gap: .35rem; border: 0; border-radius: 4px; background: transparent; padding: .3rem .4rem; font: inherit; cursor: pointer; white-space: nowrap; }
  .bonus-trigger:hover, .bonus-trigger:focus-visible { background: #f1f5f9; outline: 2px solid #2a6f9e; outline-offset: 1px; }
  .bonus-yes { color: #2e7d32; font-weight: 600; }
  .bonus-no { color: #666; }
  .detail-indicator { color: #356f91; font-size: .85em; }
  .coverage-popover { position: absolute; z-index: 50; right: 0; top: calc(100% + 5px); width: min(340px, calc(100vw - 32px)); padding: 14px; text-align: left; color: #263238; background: #fff; border: 1px solid #cbd5e1; border-radius: 8px; box-shadow: 0 8px 26px rgba(15, 23, 42, .22); white-space: normal; }
  .coverage-popover.above { top: auto; bottom: calc(100% + 5px); }
  .popover-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; }
  .close-button { border: 0; background: transparent; font-size: 1.35rem; line-height: 1; cursor: pointer; }
  .coverage-summary { margin: 12px 0 6px; }
  .snapshot-note { margin: 0 0 12px; color: #596675; font-size: .82rem; }
  .list-label { margin: 0 0 6px; font-weight: 600; }
  .missing-list { max-height: 240px; overflow-y: auto; margin: 0; padding: 0; list-style: none; }
  .missing-list li { display: flex; flex-direction: column; gap: 2px; padding: 7px 0; border-top: 1px solid #e5e7eb; }
  .class-name { overflow-wrap: anywhere; }
  .class-meta { color: #596675; font-size: .82rem; }
</style>
